// src/utils/generateSitzungPdf.js
// Sitzungsprotokoll als PDF (US-SITZ-005, ADR 0062) — zusaetzlich zum
// bestehenden Excel-Export (generateProtokoll.js, US-SITZ-002). Nutzt
// dieselbe Vorlage wie die Zahlungsanweisung im Kostentool (ADR 0054, siehe
// src/utils/pdfTemplate.js) sowie dieselben sicherheitskritischen Helfer wie
// der Excel-Export (filterForMode, attendeeDisplay) — bewusst importiert,
// nicht neu implementiert.
//
// Layout (nach mehreren Nutzer-Korrekturen, siehe ADR 0062 Nachtraege): Seite
// 1 = Sitzungsangaben + Teilnehmer:innen (+ optional Entschuldigte/Verteiler/
// Anhang, letzterer nur als Freitext ohne Datei-Upload).
// Danach folgen Notizen/Entscheide/Pendenzen (in dieser Reihenfolge) als EINE
// durchlaufende Liste. Jeder Eintrag ist eine Zeile mit bis zu vier festen
// Spalten: Nr. (optional, Modal-Schalter) | Bild (nur wenn ueberhaupt eines
// vorkommt UND "Folien anzeigen" aktiv ist) | Beschreibungstext (Notiz/
// Entscheid/Pendenz) | Zustaendigkeit + Termin ("bis wann"), falls vorhanden.
// Vor der Pendenzen-Gruppe steht statt eines reinen Textitels eine
// Tabellen-Kopfzeile mit den aktiven Spaltennamen ("Nr."/"Folie"/"Text"/
// "Zuständigkeit"); Notizen/Entscheide behalten ihren einfachen Titel.
// Entscheide erhalten zusaetzlich eine hellgraue Zeilen-Hervorhebung ueber
// die volle Breite.
import {
  MF_FIRMA,
  MF_ABSENDER,
  LOGO_RATIO,
  LEFT,
  RIGHT,
  LH,
  loadLogoDataUrl,
  loadImageAsDataUrl,
  createDrawHelpers,
} from "./pdfTemplate.js";
import { filterForMode, attendeeDisplay, formatDateDE, sanitizeFilenamePart } from "./generateProtokoll.js";
import { loadJsPdf } from "./loadJsPdf.js";
import { richTextToListText, RICH_STYLE_MARKS } from "./richText.js";

// Dynamischer statt statischer Import: azureSitzungFolienService.js zieht
// pdfjs-dist nach (fuers Rendern von PDF-Praesentationen) — das wird fuer
// einen reinen Text-Eintrag ohne angehaengtes Dokument nie gebraucht. Ein
// statischer Top-Level-Import wuerde pdfjs-dist IMMER laden, sobald dieses
// Modul importiert wird, unabhaengig davon, ob ueberhaupt ein Bild vorkommt.
let buildFolieUrlPromise;
const getBuildFolieUrl = () => {
  if (!buildFolieUrlPromise) {
    buildFolieUrlPromise = import("../services/azureSitzungFolienService.js").then((m) => m.buildFolieUrl);
  }
  return buildFolieUrlPromise;
};

const GROUPS = [
  { key: "N", label: "Notizen" },
  { key: "E", label: "Entscheide" },
  { key: "P", label: "Pendenzen" },
];

const CONTENT_BOTTOM = 280;
const CONTINUATION_TOP = 22;
const FOOTER_Y = 286;
const IMG_MAX_HEIGHT = 40; // mm, Sicherheits-Obergrenze fuer sehr hochformatige Bilder
// Abstand VOR der Trennlinie (Ende des vorherigen Eintrags) bewusst kleiner
// als NACH der Linie (Beginn des naechsten Eintrags) — bei gleichen Werten
// sass die Linie optisch zu nah am darauffolgenden Text (Nutzer-Korrektur:
// "Trennlinien oberhalb des Texts sind zu nah am Text"), da eine Textzeile
// oberhalb ihrer Grundlinie (baseline) noch Platz fuer die Versalhoehe
// braucht, unterhalb der Grundlinie aber kaum.
const GAP_TO_DIVIDER = 2; // mm zwischen letzter Textzeile und der Trennlinie
const GAP_FROM_DIVIDER = 5; // mm zwischen der Trennlinie und dem naechsten Eintrag
const PARA_BREAK_HEIGHT = 2.5; // mm Hoehe eines Absatzumbruchs (statt einer vollen Leerzeile)

// Aufzaehlungen im Beschreibungstext (Format von richTextToListText: 2
// Leerzeichen pro Ebene, dann "• " bzw. "1. "). Umbrochene Folgezeilen eines
// Punkts beginnen auf Hoehe des Texts, nicht unter dem Marker (haengender
// Einzug).
const LIST_INDENT = 5; // mm pro Ebene bzw. Marker-Breite
const LIST_ITEM_RE = /^( *)(•|\d+\.) (.*)$/;

// Fett/kursiv/unterstrichen kommen als Steuerzeichen-Marken aus
// richTextToListText (RICH_STYLE_MARKS). jsPDF kennt keine gemischten Stile
// innerhalb eines doc.text-Aufrufs und splitTextToSize misst nur mit der
// aktuellen Schrift — deshalb wird hier selbst umbrochen: Text in Stueck-Runs
// zerlegen, Woerter (ggf. aus mehreren Runs, z.B. halb fett) mit der Breite
// ihres jeweiligen Stils messen und greedy auf Zeilen verteilen.
const MARK_TO_STYLE = Object.fromEntries(
  Object.entries(RICH_STYLE_MARKS).flatMap(([k, [on, off]]) => [
    [on, [k, true]],
    [off, [k, false]],
  ])
);

const fontStyleOf = (s) =>
  s.bold && s.italic ? "bolditalic" : s.bold ? "bold" : s.italic ? "italic" : "normal";

const parseRuns = (str) => {
  const runs = [];
  const state = { bold: false, italic: false, underline: false };
  let buf = "";
  const flush = () => {
    if (buf) runs.push({ text: buf, ...state });
    buf = "";
  };
  for (const ch of str) {
    const mark = MARK_TO_STYLE[ch];
    if (mark) {
      flush();
      state[mark[0]] = mark[1];
    } else {
      buf += ch;
    }
  }
  flush();
  return runs;
};

const stripMarks = (str) => [...str].filter((ch) => !MARK_TO_STYLE[ch]).join("");

// Runs → Zeilen aus Stuecken [{text, bold, italic, underline, w}].
const wrapRuns = (doc, runs, maxWidth) => {
  // OHNE Kerning messen: getTextWidth zieht Kerning-Paare ab ("Ve", "To"),
  // doc.text zeichnet aber ungekernt — Woerter wie "Velo" waeren sonst
  // breiter als gemessen und wuerden das folgende Leerzeichen ueberdecken.
  const measure = (piece) => {
    doc.setFont("helvetica", fontStyleOf(piece));
    return (doc.getStringUnitWidth(piece.text, { kerning: {} }) * doc.getFontSize()) / doc.internal.scaleFactor;
  };
  // Tokens: Leerraum einzeln, Nicht-Leerraum zu Wortgruppen zusammengefasst.
  const tokens = [];
  for (const run of runs) {
    for (const part of run.text.match(/\s+|\S+/g) || []) {
      const piece = { ...run, text: part };
      piece.w = measure(piece);
      const last = tokens[tokens.length - 1];
      if (/^\s/.test(part)) tokens.push({ space: true, pieces: [piece], w: piece.w });
      else if (last && !last.space) {
        last.pieces.push(piece);
        last.w += piece.w;
      } else tokens.push({ space: false, pieces: [piece], w: piece.w });
    }
  }

  const lines = [];
  let line = [];
  let w = 0;
  const newLine = () => {
    while (line.length && /^\s+$/.test(line[line.length - 1].text)) line.pop();
    lines.push(line);
    line = [];
    w = 0;
  };
  for (const tok of tokens) {
    if (tok.space) {
      if (line.length) {
        line.push(...tok.pieces);
        w += tok.w;
      }
      continue;
    }
    if (line.length && w + tok.w > maxWidth) newLine();
    if (tok.w <= maxWidth) {
      line.push(...tok.pieces);
      w += tok.w;
      continue;
    }
    // Einzelnes Wort breiter als die Spalte (z.B. lange URL): zeichenweise.
    for (const piece of tok.pieces) {
      for (const ch of piece.text) {
        const c = { ...piece, text: ch };
        c.w = measure(c);
        if (line.length && w + c.w > maxWidth) newLine();
        line.push(c);
        w += c.w;
      }
    }
  }
  if (line.length) newLine();
  doc.setFont("helvetica", "normal");
  return lines;
};

// Zerlegt den Beschreibungstext in zeichenfertige Zeilen {pieces, x, marker,
// markerX}. Leere Zeilen (pieces leer) bleiben Absatzumbrueche.
const layoutDescription = (doc, text, x, width) => {
  const out = [];
  // Tiptap speichert Leerzeichen teils als &nbsp; (U+00A0) — jsPDF zeichnet
  // das in Helvetica als (fast) unsichtbaren Abstand, die Woerter kleben
  // dann zusammen ("Vorstellungsrunde.Philippe").
  for (const raw of text.replace(/ /g, " ").split("\n")) {
    if (stripMarks(raw).trim() === "") {
      out.push({ pieces: [], x });
      continue;
    }
    const m = raw.match(LIST_ITEM_RE);
    if (m) {
      const markerX = x + (m[1].length / 2) * LIST_INDENT;
      const textX = markerX + LIST_INDENT;
      wrapRuns(doc, parseRuns(m[3]), x + width - textX).forEach((pieces, i) => {
        out.push({ pieces, x: textX, marker: i === 0 ? m[2] : null, markerX });
      });
      continue;
    }
    // Normaler Absatz bzw. weiterer Absatz innerhalb eines Listenpunkts
    // (eingerueckt auf dessen Textspalte).
    const lead = raw.match(/^ */)[0].length;
    const lineX = x + (lead / 2) * LIST_INDENT;
    for (const pieces of wrapRuns(doc, parseRuns(raw.slice(lead)), x + width - lineX)) {
      out.push({ pieces, x: lineX });
    }
  }
  return out;
};

const drawPieces = (doc, pieces, x, y) => {
  let xx = x;
  for (const p of pieces) {
    doc.setFont("helvetica", fontStyleOf(p));
    doc.text(p.text, xx, y);
    if (p.underline && p.text.trim()) {
      doc.setLineWidth(0.2);
      doc.setDrawColor(0);
      doc.line(xx, y + 0.7, xx + p.w, y + 0.7);
    }
    xx += p.w;
  }
  doc.setFont("helvetica", "normal");
};

// Bis zu vier feste Spalten je Eintrags-Zeile: Nr. | Bild | Text |
// Zustaendigkeit+Termin. Nr. und Bild sind optional (je nach Modal-Option
// bzw. ob ueberhaupt Bilder vorkommen) — `computeColumns` berechnet die
// tatsaechlichen Positionen/Breiten je nachdem, welche aktiv sind, damit die
// Text-Spalte den frei werdenden Platz uebernimmt.
const COL_GUTTER = 4;
const NR_COL_WIDTH = 12;
const IMG_COL_WIDTH = 24;
const RESP_COL_WIDTH = 40;

const computeColumns = ({ showNummer, showImgCol }) => {
  let x = LEFT;
  const cols = {};
  if (showNummer) {
    cols.nr = { x, width: NR_COL_WIDTH };
    x += NR_COL_WIDTH + COL_GUTTER;
  }
  if (showImgCol) {
    cols.img = { x, width: IMG_COL_WIDTH };
    x += IMG_COL_WIDTH + COL_GUTTER;
  }
  const textWidth = RIGHT - x - COL_GUTTER - RESP_COL_WIDTH;
  cols.text = { x, width: textWidth };
  cols.resp = { x: x + textWidth + COL_GUTTER, width: RESP_COL_WIDTH };
  return cols;
};

// Loest die Bildquelle eines Eintrags auf: zuerst ein direkt angehaengtes
// Dokument (dokumentBlobPath, US-PEND-011), sonst — falls die Praesentation
// gezeigt werden soll — der Praesentations-Anker ("slide:<folieId>" oder
// "gap:<folieId>"; "gap:start" hat keine zugehoerige Folie). Der Vergleich
// `entry.sitzungId === sitzungId` ist zwingend: Anker sind sitzungsuebergreifend
// NICHT eindeutig, jede Praesentation nummeriert wieder ab folie_0001 (ADR
// 0059, Bugfix-Punkt 10) — ohne diese Pruefung koennte ein Eintrag faelschlich
// das Bild einer fremden Sitzung zeigen.
export const resolveEntryImageUrl = async (entry, { folien, sitzungId, showFolien }) => {
  if (!showFolien) return null;
  if (entry.dokumentBlobPath) {
    const buildFolieUrl = await getBuildFolieUrl();
    return buildFolieUrl(entry.dokumentBlobPath);
  }
  const anchor = entry.praesentationAnchor || "";
  if (!anchor || entry.sitzungId !== sitzungId) return null;
  const [kind, ref] = anchor.split(":");
  if (kind !== "slide" && kind !== "gap") return null;
  if (kind === "gap" && ref === "start") return null;
  const folie = (folien || []).find((f) => f.id === ref);
  return folie ? folie.url : null;
};

// Laedt fuer jeden Eintrag (falls vorhanden) sein Bild als Data-URL VOR dem
// eigentlichen Zeichnen — haelt die Zeichenschleife synchron. Ein einzelnes
// nicht ladbares Bild (Netzwerk/CORS) darf den Rest des PDFs nie verhindern,
// daher scheitert loadImageAsDataUrl niemals mit einem throw (siehe pdfTemplate.js).
//
// Dieselbe Folie (mehrere Notizen/Pendenzen koennen denselben "slide:"-Anker
// teilen) wird nur beim ERSTEN Eintrag angezeigt, der sie referenziert — im
// Sitzungsmodus selbst steht eine Folie ebenfalls nur einmal, mit allen
// zugeordneten Eintraegen daneben aufgelistet (siehe SitzungPraesentation.jsx).
// Ohne dieses Deduplizieren wurde dieselbe (oft hochformatige) Folie pro
// Eintrag erneut eingebettet und reservierte jedes Mal ihre volle Hoehe,
// obwohl der zugehoerige Text meist kurz ist — das erzeugte den grossen,
// unnoetigen Leerraum, den Nutzer:innen als "komisch" gemeldet haben.
// `entries` MUSS bereits in der finalen Anzeige-Reihenfolge uebergeben
// werden (siehe Aufrufer), sonst "gewinnt" eine zufaellige statt die erste
// sichtbare Zeile.
// Exportiert (statt nur intern), damit ein Node-Smoke-Test das Dedup direkt
// beobachten kann, ohne die Kernfunktion mit Fixture-Daten durchzuspielen.
export const preloadImages = async (entries, { folien, sitzungId, showFolien }) => {
  const urls = await Promise.all(
    entries.map((e) => resolveEntryImageUrl(e, { folien, sitzungId, showFolien }))
  );
  const seen = new Set();
  const dedupedUrls = urls.map((u) => {
    if (!u || seen.has(u)) return null;
    seen.add(u);
    return u;
  });
  const dataUrls = await Promise.all(
    dedupedUrls.map((u) => (u ? loadImageAsDataUrl(u) : Promise.resolve(null)))
  );
  return entries.map((e, i) => ({ ...e, imageDataUrl: dataUrls[i] }));
};

// Reiner Seiten-Cursor (kein Spalten-Konzept mehr — jeder Eintrag ist eine
// volle-Breite-Zeile mit drei festen Sub-Spalten, siehe drawEntryRow).
const createCursor = (doc, { startY, startPage = 1, sitzung }) => {
  let page = startPage;
  let y = startY;

  const drawRunningHeader = () => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90);
    doc.text(`${sitzung.art || ""} Nr. ${sitzung.nr || ""} · ${formatDateDE(sitzung.datum)}`, LEFT, 12);
    doc.setTextColor(0);
  };

  return {
    get page() {
      return page;
    },
    get y() {
      return y;
    },
    set y(v) {
      y = v;
    },
    ensureSpace(h) {
      if (y + h > CONTENT_BOTTOM) {
        // Die Kopfzeile setzt 8pt — Schrift des laufenden Eintrags danach
        // wiederherstellen, sonst laeuft der Rest des Texts auf der
        // Folgeseite kleiner weiter (Nutzer-Meldung 2026-10-26).
        const { fontName, fontStyle } = doc.getFont();
        const fontSize = doc.getFontSize();
        doc.addPage();
        page += 1;
        y = CONTINUATION_TOP;
        drawRunningHeader();
        doc.setFont(fontName, fontStyle);
        doc.setFontSize(fontSize);
      }
    },
  };
};

// Zeichnet einen Eintrag (Notiz/Entscheid/Pendenz) als Zeile mit den in
// `cols` aktiven Spalten (Nr. optional | Bild optional | Text | Zustaendigkeit
// +Termin). Nr./Bild/Zustaendigkeit werden EINMAL am Anfang der Zeile
// platziert (kurzer, praktisch nie umbrechender Inhalt); der Beschreibungs-
// text wird zeilenweise gezeichnet, damit ein ungewoehnlich langer Text auch
// ueber eine Seitengrenze hinweg umbrechen kann, ohne dass Nr./Bild/
// Zustaendigkeit wiederholt werden muessten. Die Entscheid-Hervorhebung wird
// PRO TEXTZEILE als volle-Breite-Rechteck hinter dem Text gezeichnet (deckt
// bei Bedarf zusaetzlich den Nr.-/Bild-/Zustaendigkeits-Bereich ab) —
// dadurch endet/beginnt sie automatisch korrekt an Seitenkanten, ohne einen
// separaten Mess-Durchlauf zu benoetigen.
const drawEntryRow = (doc, { font, txt }, cursor, entry, group, cols) => {
  const isEntscheid = group.key === "E";

  let imgW = 0;
  let imgH = 0;
  if (cols.img && entry.imageDataUrl) {
    try {
      const props = doc.getImageProperties(entry.imageDataUrl);
      const ratio = props.width && props.height ? props.width / props.height : 1;
      // Breite der Spalte moeglichst ausnutzen (lesbar statt winzig) — nur
      // bei sehr hochformatigen Bildern (Ratio < ~0.4) zusaetzlich die Hoehe
      // deckeln, damit ein Hochformat-Dokument die Zeile nicht sprengt.
      imgW = cols.img.width;
      imgH = imgW / ratio;
      if (imgH > IMG_MAX_HEIGHT) {
        imgH = IMG_MAX_HEIGHT;
        imgW = imgH * ratio;
      }
    } catch {
      imgW = 0;
      imgH = 0;
    }
  }

  font("normal", 8.5);
  const respLines = [];
  if (entry.responsible) respLines.push(...doc.splitTextToSize(entry.responsible, cols.resp.width));
  if (entry.terminText) respLines.push(entry.terminText);

  const headerExtra = Math.max(LH, imgH, respLines.length * LH);
  const highlightRow = (rowY, rowH) => {
    if (!isEntscheid) return;
    doc.setFillColor(242, 242, 242);
    doc.rect(LEFT - 1.5, rowY - rowH + 1.2, RIGHT - LEFT + 3, rowH, "F");
  };

  // Platz fuer die erste Zeile UND das ggf. hohe Bild/Zustaendigkeit
  // sicherstellen, bevor die feste y-Position dieser Zeile fixiert wird.
  cursor.ensureSpace(headerExtra);
  const rowTop = cursor.y;
  const rowStartPage = cursor.page;

  highlightRow(rowTop + LH, LH);
  if (cols.nr) {
    font("bold", 8.5);
    txt(`${entry.nummer}`, cols.nr.x, rowTop);
  }
  if (imgH > 0) {
    try {
      doc.addImage(entry.imageDataUrl, "JPEG", cols.img.x, rowTop, imgW, imgH);
    } catch {
      /* Bild ist optional, PDF darf deswegen nicht scheitern */
    }
  }
  font("normal", 8.5);
  let ry = rowTop;
  for (const line of respLines) {
    txt(line, cols.resp.x + cols.resp.width, ry, { align: "right" });
    ry += LH;
  }

  font("normal", 9);
  const lines = layoutDescription(doc, entry.beschreibungText || "(kein Text)", cols.text.x, cols.text.width);
  // Linksbuendiger Blocksatz (Nutzer-Korrektur: das gestreckte align:"justify"
  // wirkte nicht wie gewuenscht) — jede Zeile startet links an der Spalte,
  // ohne Wortabstand-Streckung auf die volle Breite. Eine LEERE Zeile
  // (Absatzumbruch im Rich-Text, z.B. zwei Absaetze in einer Notiz) bekommt
  // NUR einen kleineren Abstand statt einer vollen Zeilenhoehe — auf
  // Nutzer-Wunsch OHNE eigene Trennlinie, Linien stehen ausschliesslich
  // zwischen ganzen Eintraegen (siehe Hauptschleife).
  for (const line of lines) {
    if (line.pieces.length === 0) {
      cursor.ensureSpace(PARA_BREAK_HEIGHT);
      cursor.y += PARA_BREAK_HEIGHT;
      continue;
    }
    cursor.ensureSpace(LH);
    highlightRow(cursor.y, LH);
    if (line.marker) txt(line.marker, line.markerX, cursor.y);
    drawPieces(doc, line.pieces, line.x, cursor.y);
    cursor.y += LH;
  }

  // Das Bild/die Zustaendigkeit stehen NUR auf der Start-Seite dieser Zeile
  // (rowTop). Ist der Beschreibungstext so lang, dass er auf eine oder
  // mehrere Folgeseiten umbricht, ist `rowTop + headerExtra` eine
  // Koordinate auf der ALTEN Seite und darf NIE mit dem aktuellen
  // `cursor.y` (jetzt auf einer neuen Seite, ab `CONTINUATION_TOP`
  // gezaehlt) verglichen werden — das haette faelschlich einen Sprung auf
  // einen Leftover-y-Wert der Startseite ausgeloest und einen großen, vom
  // Nutzer als "Luecke" gemeldeten Leerraum erzeugt (Bug gefunden anhand
  // eines echten, mehrseitigen Notiz-Eintrags mit Folien-Anker).
  if (cursor.page === rowStartPage) {
    const asideBottom = rowTop + headerExtra;
    if (asideBottom > cursor.y) cursor.y = asideBottom;
  }
};

// Zeichnet die Tabellen-Kopfzeile ("Nr." | "Folie" | "Text" |
// "Zuständigkeit") anstelle des bisherigen reinen "Pendenzen"-Titels — nur
// die Spalten, die auch tatsaechlich aktiv sind (siehe computeColumns).
const drawColumnHeader = (doc, { font, txt, hr }, cursor, cols) => {
  font("bold", 8.5);
  if (cols.nr) txt("Nr.", cols.nr.x, cursor.y);
  if (cols.img) txt("Folie", cols.img.x, cursor.y);
  txt("Text", cols.text.x, cursor.y);
  txt("Zuständigkeit", cols.resp.x + cols.resp.width, cursor.y, { align: "right" });
  cursor.y += 1.5;
  hr(cursor.y, LEFT, RIGHT, 0.2);
  cursor.y += LH + 1;
};

// Kernfunktion — nimmt bereits aufbereitete Eintraege entgegen (Klartext
// statt Rich-Text-HTML, Zustaendigkeit/Termin bereits als fertige Strings).
// Bewusst von `buildSitzungPdf` getrennt exportiert: `richTextToListText`
// braucht ein DOM (document.createElement) und laeuft nicht unter Node — ein
// Node-Smoke-Test kann diese Kernfunktion direkt mit Klartext-Fixtures
// aufrufen, um Seitenumbruch, Bild-Platzierung und Hervorhebung zu
// verifizieren, ohne ein DOM zu benoetigen.
export async function buildSitzungPdfCore({
  sitzung,
  project,
  entries,
  showNotizen = true,
  showFolien = true,
  showNummer = false,
  entschuldigte = [],
  verteilerText = "",
  anhangText = "",
  folien = [],
}) {
  const jsPDF = await loadJsPdf();
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  const { font, txt, hr, block } = createDrawHelpers(doc);

  // Reihenfolge VOR dem Bild-Laden festlegen (Notizen/Entscheide/Pendenzen,
  // je nummer-sortiert) — preloadImages dedupliziert wiederholte Folien in
  // genau dieser Reihenfolge (siehe dort), muss also bereits die finale
  // Anzeige-Reihenfolge sehen, nicht die rohe Eingabe-Reihenfolge.
  const orderedEntries = GROUPS.flatMap((group) => {
    if (group.key === "N" && !showNotizen) return [];
    return entries
      .filter((e) => e.typ === group.key)
      .sort((a, b) => a.nummer - b.nummer);
  });

  const enrichedEntries = await preloadImages(orderedEntries, {
    folien,
    sitzungId: sitzung.id,
    showFolien,
  });
  // Die Bild-Spalte wird nur reserviert, wenn es ueberhaupt etwas zu zeigen
  // gibt — weder ein deaktiviertes "Folien anzeigen" noch eine Sitzung ganz
  // ohne Praesentation/Dokument-Anhaenge sollen ungenutzten Leerraum erzeugen.
  const showImgCol = showFolien && enrichedEntries.some((e) => e.imageDataUrl);
  const cols = computeColumns({ showNummer, showImgCol });

  // --- Seite 1: Logo, Titel, Sitzungsangaben, Teilnehmer:innen -------------
  const logo = await loadLogoDataUrl();
  const logoW = 30;
  if (logo) {
    try {
      doc.addImage(logo, "PNG", RIGHT - logoW, 12, logoW, logoW * LOGO_RATIO);
    } catch {
      /* Logo ist optional */
    }
  }

  font("bold", 15);
  txt("Sitzungsprotokoll", LEFT, 22);
  font("normal", 11);
  txt(`${sitzung.art || ""} Nr. ${sitzung.nr || ""}`, LEFT, 29);

  let y = 40;
  font("bold", 9.5);
  txt("Datum:", LEFT, y);
  font("normal", 9.5);
  const zeitSuffix = sitzung.vonZeit && sitzung.bisZeit ? `, ${sitzung.vonZeit}–${sitzung.bisZeit} Uhr` : "";
  txt(`${formatDateDE(sitzung.datum)}${zeitSuffix}`, 45, y);
  y += LH + 1;

  if (sitzung.ort) {
    font("bold", 9.5);
    txt("Ort:", LEFT, y);
    font("normal", 9.5);
    txt(sitzung.ort, 45, y);
    y += LH + 1;
  }

  font("bold", 9.5);
  txt("Projekt:", LEFT, y);
  font("normal", 9.5);
  txt(`${project?.code || ""} – ${project?.title || ""}`, 45, y);
  y += LH + 4;

  hr(y);
  y += 6;

  font("bold", 10);
  txt("Teilnehmer:innen", LEFT, y);
  y += LH + 1;
  font("normal", 9);
  const anwesende = sitzung.anwesende || [];
  if (anwesende.length === 0) {
    txt("–", LEFT, y);
    y += LH;
  } else {
    for (const a of anwesende) {
      txt(attendeeDisplay(a), LEFT, y);
      y += LH;
    }
  }
  y += 2;

  if (entschuldigte.length > 0) {
    font("bold", 10);
    txt("Entschuldigt", LEFT, y);
    y += LH + 1;
    font("normal", 9);
    for (const a of entschuldigte) {
      txt(attendeeDisplay(a), LEFT, y);
      y += LH;
    }
    y += 2;
  }

  if (verteilerText && verteilerText.trim()) {
    font("bold", 10);
    txt("Verteiler", LEFT, y);
    y += LH + 1;
    font("normal", 9);
    const lines = doc.splitTextToSize(verteilerText.trim(), RIGHT - LEFT);
    y = block(lines, LEFT, y);
    y += 2;
  }

  // Anhang: reines Textfeld (kein Datei-Upload, US-SITZ-005 Nachtrag) — z.B.
  // "Plan Erdgeschoss V2, Kostenschaetzung.xlsx" als Freitext-Hinweis, welche
  // Beilagen zusaetzlich zum Protokoll zirkulieren, ohne dass diese Dateien
  // tatsaechlich eingebettet werden muessten.
  if (anhangText && anhangText.trim()) {
    font("bold", 10);
    txt("Anhang", LEFT, y);
    y += LH + 1;
    font("normal", 9);
    const lines = doc.splitTextToSize(anhangText.trim(), RIGHT - LEFT);
    y = block(lines, LEFT, y);
    y += 2;
  }

  y += 2;
  hr(y);
  y += 8;

  // --- Durchlaufende Liste: Notizen, Entscheide, Pendenzen -----------------
  let bodyStartY = y;
  let bodyStartPage = 1;
  if (bodyStartY > CONTENT_BOTTOM - 20) {
    doc.addPage();
    bodyStartPage = 2;
    bodyStartY = CONTINUATION_TOP;
  }
  const cursor = createCursor(doc, { startY: bodyStartY, startPage: bodyStartPage, sitzung });

  let anyEntries = false;
  for (const group of GROUPS) {
    if (group.key === "N" && !showNotizen) continue;
    const items = enrichedEntries
      .filter((e) => e.typ === group.key)
      .sort((a, b) => a.nummer - b.nummer);
    if (items.length === 0) continue;
    anyEntries = true;

    // Mindestplatz fuer Ueberschrift + einen ersten kleinen Eintrag freihalten,
    // damit keine Ueberschrift verwaist am Seitenende stehen bleibt.
    cursor.ensureSpace(LH + 10);
    if (group.key === "P") {
      // Ersetzt den frueheren reinen "Pendenzen"-Titel durch die
      // Tabellen-Kopfzeile — die Spaltenbedeutung (Nr./Folie/Text/
      // Zustaendigkeit) gilt strukturell fuer alle drei Gruppen gleich,
      // wird hier aber einmal explizit benannt.
      drawColumnHeader(doc, { font, txt, hr }, cursor, cols);
    } else {
      font("bold", 11);
      txt(group.label, LEFT, cursor.y);
      cursor.y += LH + 2;
    }

    for (const entry of items) {
      drawEntryRow(doc, { font, txt }, cursor, entry, group, cols);
      // Statt einer reinen Leerzeile zwischen Eintraegen (wirkte als "leerer
      // Abschnitt"/Luecke, insbesondere wenn die Zustaendigkeits-Spalte bei
      // Notizen/Entscheiden meist leer ist): duenne Trennlinie, deutlich
      // duenner als die Kopfzeilen-Linie (0.2mm) bzw. die Sitzungsangaben-
      // Trenner (0.3mm) oben auf Seite 1. Bricht die Seite genau an dieser
      // Stelle um, steht bereits die Seitenkante als Trenner da — eine Linie
      // direkt unter der laufenden Kopfzeile waere ueberfluessig/verwirrend.
      const pageBeforeDivider = cursor.page;
      cursor.y += GAP_TO_DIVIDER;
      cursor.ensureSpace(GAP_FROM_DIVIDER);
      if (cursor.page === pageBeforeDivider) {
        hr(cursor.y, LEFT, RIGHT, 0.1);
        cursor.y += GAP_FROM_DIVIDER;
      }
    }
    cursor.y += 4;
  }

  if (!anyEntries) {
    font("italic", 9);
    txt("Keine Einträge zu dieser Sitzung.", LEFT, cursor.y);
  }

  // --- Fusszeile + Seitenzahl (Stempel-Pass ueber alle Seiten) -------------
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    font("normal", 8);
    doc.setTextColor(70);
    txt(MF_ABSENDER, 105, FOOTER_Y, { align: "center" });
    txt(`Seite ${i} von ${totalPages}`, RIGHT, FOOTER_Y, { align: "right" });
    doc.setTextColor(0);
  }

  doc.setProperties({
    title: `Sitzungsprotokoll ${sitzung.art} Nr. ${sitzung.nr}`,
    subject: project?.title || "",
    author: MF_FIRMA,
    creator: `${MF_FIRMA} INTRA`,
  });

  return doc;
}

// Oeffentliche API (Browser): loest intern/extern-Filter, Rich-Text-Konvertierung
// und die Zustaendigkeits-/Termin-Anzeige auf, bevor die Kernfunktion das PDF baut.
export async function buildSitzungPdf({
  sitzung,
  project,
  pendenzenDieserSitzung,
  modus = "intern",
  showNotizen = true,
  showFolien = true,
  showNummer = false,
  entschuldigte = [],
  verteilerText = "",
  anhangText = "",
  folien = [],
}) {
  const gefiltert = filterForMode(pendenzenDieserSitzung, modus);
  const entries = gefiltert.map((p) => ({
    ...p,
    beschreibungText: richTextToListText(p.beschreibung),
    // Extern: verantwortlichExtern nutzen (maskiert Team-Namen bereits zu
    // "Malheur&Fortuna", zeigt externe CRM-Personen mit echtem Namen). Ist
    // dieses Feld ausnahmsweise leer, obwohl jemand zugewiesen ist (z.B.
    // Altdaten/ein Erfassungsweg, der es nie gesetzt hat), NIE die volle
    // interne Zuweisung durchsickern lassen — stattdessen der sichere
    // Default "Malheur&Fortuna" statt einer leeren Spalte.
    responsible:
      modus === "extern"
        ? p.verantwortlichExtern || (p.verantwortlich ? "Malheur&Fortuna" : "")
        : p.verantwortlich,
    // Korrigierte Deadline hat Vorrang vor der urspruenglichen (analog Excel-
    // Export, der beide als eigene Spalten fuehrt) — hier nur eine Zeile,
    // daher Prioritaet statt beider Werte.
    terminText: p.terminNeu
      ? `bis ${formatDateDE(p.terminNeu)}`
      : p.terminSoll
        ? `bis ${formatDateDE(p.terminSoll)}`
        : "",
  }));
  return buildSitzungPdfCore({
    sitzung,
    project,
    entries,
    showNotizen,
    showFolien,
    showNummer,
    entschuldigte,
    verteilerText,
    anhangText,
    folien,
  });
}

export const sitzungPdfFilename = (project, sitzung) =>
  `Sitzungsprotokoll_${sanitizeFilenamePart(project?.code)}_${sanitizeFilenamePart(sitzung?.art)}_${
    sitzung?.datum || new Date().toISOString().slice(0, 10)
  }.pdf`;

// PDF erzeugen und im Browser als Download ausloesen.
export async function downloadSitzungPdf(data) {
  const doc = await buildSitzungPdf(data);
  doc.save(sitzungPdfFilename(data.project, data.sitzung));
}
